import { WPSClient, StorefrontItem as Enrichment, RequestContext } from 'DVWebNodeUtils'
import { v2Request } from '../utils/EnrichItemMetadataUtils'
import { counter } from '../../../util/csm/csm'

const QUEUE_LIMIT = 30

export type EnrichmentMap = { [title: string]: Enrichment }
type OnEnrichTitleCallback = (newEnrichments: EnrichmentMap) => void
type EnrichmentCallbackList = (OnEnrichTitleCallback | undefined)[] 

export class GlobalQueueHandler {
  ctx: RequestContext
  queue: string[] = []
  finished: string[] = []
  static enrichmentMap: EnrichmentMap = {}
  nextEnrich?: number
  timeout: number
  static callbacksOnEnrich: EnrichmentCallbackList = []

  constructor(context: RequestContext, timeout = 1500) {
    this.ctx = context
    this.timeout = timeout
  }

  enqueue(titleID: string) {
    if (this.queue.indexOf(titleID) > -1 || this.finished.indexOf(titleID) > -1)
      return
    this.queue.push(titleID)
    // If we have filled the queue, call immediately
    if (this.queueFull())
      this.handleQueue()
    //Otherwise, set the timer
    else if (!this.nextEnrich)
      this.setTimer()
  }

  // undefined is a syntactic convenience to allow passing of titleIDs directly, where an item's titleID is type string?
  enrichmentFor(title: string | undefined): Enrichment | undefined {
    if (!title)
      return undefined
    return GlobalQueueHandler.enrichmentMap[title]
  }

  setTimer() {
    this.nextEnrich = window.setTimeout(this.handleQueue, this.timeout)
  }

  /**
   * Executes callback with new enrichments on EVERY enrichment 
   * change until disconnected
   * 
   * @param {Function} callback The function to execute, with the new enrichments passed as a param.
   * @return {number} A number representing the index of this callback (to use for disconnection).
   */
  notifyOnChange(callback: (item: EnrichmentMap) => void) {
    const length = GlobalQueueHandler.callbacksOnEnrich.push(callback)

    // Return the index the listener is at, to allow disconnection
    return length - 1
  }

  disconnectNotifier(index: number) {
    GlobalQueueHandler.callbacksOnEnrich[index] = undefined
  }

  broadcastChanges(response: EnrichmentMap) {
    const callbacks = GlobalQueueHandler.callbacksOnEnrich
    callbacks.forEach(listener => {
      listener && listener(response)
    })
  }

  queueFull() {
    return this.queue.length >= QUEUE_LIMIT
  }

  clearTimer() {
    window.clearTimeout(this.nextEnrich)
    this.nextEnrich = undefined
  }

  handleQueue = () => {
    if (!this.ctx || !this.queue.length)
      return

    const thisBatch = this.queue.slice(0, QUEUE_LIMIT)
    this.queue = this.queue.slice(QUEUE_LIMIT)
    this.finished = [...this.finished, ...thisBatch]

    counter("DVWeb::EIMBatchCall", 1, false)
    counter("DVWeb::EIMBatchSize", thisBatch.length, false)
    const wps = new WPSClient()
    const request = v2Request(thisBatch, "HOVER", true, true, true, true)
    wps.EnrichItemMetadata({
      context: this.ctx,
      ...request
    })
      .then((response) => {
        this.clearTimer()

        GlobalQueueHandler.enrichmentMap = { ...GlobalQueueHandler.enrichmentMap, ...response.enrichments }

        this.broadcastChanges(response.enrichments)
        if (this.queueFull())
          this.handleQueue()
        else if (this.queue.length > 0)
          this.setTimer()
      })
  }
}
